import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { getPendingCount } from '../../services/apiTutor';
import { useAuthStore } from '../../store/useAuthStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import { useSearchStore } from '../../store/useSearchStore';
import { resolveMediaUrl } from '../../lib/utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../brand/Logo';

dayjs.extend(relativeTime);

export default function GlobalHeader() {
    const { user } = useAuthStore();
    const { notifications, unreadCount, fetchNotifications, startListening, markAsRead, startPolling } = useNotificationStore();
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const notifRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Search state
    const { searchQuery, setSearchQuery } = useSearchStore();
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchContainerRef = useRef(null);
    const isContactsPage = location.pathname === '/contacts';

    // Clear search query on route changes
    useEffect(() => {
        setSearchQuery('');
        setShowDropdown(false);
        setSearchResults([]);
    }, [location.pathname, setSearchQuery]);

    // Handle click outside of search dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Perform search query when input changes (sensitive search starting at 3 characters)
    useEffect(() => {
        if (isContactsPage || searchQuery.trim().length < 3) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        const delayDebounceFn = setTimeout(async () => {
            setIsSearching(true);
            try {
                const { searchContacts } = await import('../../services/apiContacts');
                const results = await searchContacts(searchQuery);
                setSearchResults(results || []);
                setShowDropdown(true);
            } catch (error) {
                console.error("Error searching contacts:", error);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, isContactsPage]);

    // Menu logic
    const role = user?.role;
    const isSuperAdmin = role === 'super_admin';
    const isManager    = ['admin', 'manager'].includes(role);

    const tenant = useAuthStore(state => state.tenant);
    const isTutorEnabled = tenant?.settings?.is_tutor_enabled ?? false;

    // Con la moderación encendida, el piloto automático redacta solo y deja el
    // borrador esperando. El menú no mostraba nada, así que la única forma de
    // enterarse era entrar a Tutor: veinte borradores estuvieron un día ahí sin
    // que nadie supiera. Se consulta sólo si el módulo está activo.
    const { data: tutorPendientes = 0 } = useQuery({
        queryKey: ['tutor-pending-count'],
        queryFn: getPendingCount,
        enabled: isTutorEnabled && !isSuperAdmin,
        refetchInterval: 60000,
        staleTime: 30000,
        // Un contador que no carga no puede romper el encabezado.
        retry: false,
        meta: { errorPolicy: 'silent' },
    });

    const agentItems = [
        { name: 'Mi Cartera', icon: 'person_pin', path: '/my-portfolio' },
        { name: 'Leads AI', icon: 'bolt', path: '/leads' },
        { name: 'Contactos', icon: 'group', path: '/contacts', fill: true },
        { name: 'Mensajes', icon: 'chat', path: '/chat' },
        ...(isTutorEnabled ? [{ name: 'Tutor AI', icon: 'visibility', path: '/tutor', badge: tutorPendientes }] : []),
        { name: 'Plantillas', icon: 'description', path: '/settings/templates' },
        { name: 'Negocios', icon: 'work', path: '/deals' },
    ];

    const managerItems = [
        { name: 'Panel Manager', icon: 'bar_chart_4_bars', path: '/manager' },
        { name: 'Leads AI', icon: 'bolt', path: '/leads' },
        { name: 'Contactos', icon: 'group', path: '/contacts', fill: true },
        { name: 'Mensajes', icon: 'chat', path: '/chat' },
        ...(isTutorEnabled ? [{ name: 'Tutor AI', icon: 'visibility', path: '/tutor', badge: tutorPendientes }] : []),
        { name: 'Usuarios', icon: 'manage_accounts', path: '/users' },
        { name: 'Configuración IA', icon: 'psychology', path: '/settings/ai' },
        { name: 'Config. General', icon: 'tune', path: '/config' },
        { name: 'Plantillas', icon: 'description', path: '/settings/templates' },
        { name: 'Sinc. Inventario', icon: 'sync_alt', path: '/settings/inventory' },
        { name: 'Sandbox IA', icon: 'science', path: '/ai-sandbox' },
        { name: 'Negocios', icon: 'work', path: '/deals' },
    ];

    const navItems = isManager ? managerItems : agentItems;

    const handleLogout = async () => {
        const { logout } = useAuthStore.getState();
        await logout();
        navigate('/login');
    };

    useEffect(() => {
        if (!user?.id) return;
        fetchNotifications();
        const stopListening = startListening(user.id);
        const stopPolling = startPolling();
        return () => {
            if (stopListening) stopListening();
            if (stopPolling) stopPolling();
        };
    }, [user?.id, fetchNotifications, startListening, startPolling]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notifRef.current && !notifRef.current.contains(event.target)) {
                setIsNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNotificationClick = (notif) => {
        markAsRead(notif.id);
        setIsNotifOpen(false);
        
        const type = notif.data?.type;
        const contactId = notif.data?.contact_id;
        const actionUrl = notif.data?.action_url;

        if (actionUrl) {
            navigate(actionUrl);
        } else if (contactId) {
            navigate(`/contacts/${contactId}/360`);
        }
    };

    const initials = (user?.name || user?.first_name || '')?.split(' ')?.filter(Boolean)?.map(n => n[0])?.slice(0, 2)?.join('')?.toUpperCase() || '?';

    return (
        <>
        <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-primary/10 bg-white dark:bg-background-dark px-4 md:px-10 py-3 sticky top-0 z-50 transition-colors h-[69px]">
            {/* Left: Mobile Menu & Logo */}
            <div className="flex items-center gap-3 lg:hidden">
                <button 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 -ml-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                    <span className="material-symbols-outlined text-2xl">{isMobileMenuOpen ? 'close' : 'menu'}</span>
                </button>
                <div 
                    onClick={() => navigate('/')}
                    className="size-8 flex items-center justify-center rounded-lg bg-primary text-white shadow-sm cursor-pointer"
                >
                    <Logo variante="simple" className="size-6" />
                </div>
            </div>

            {/* Left spacing for desktop layout balance */}
            <div className="hidden lg:block w-8"></div>

            {/* Center: Search */}
            <div className="hidden md:flex flex-1 justify-center max-w-md mx-8 relative" ref={searchContainerRef}>
                <label className="flex w-full h-10">
                    <div className="flex w-full items-stretch rounded-full bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 focus-within:border-primary/50 transition-colors">
                        <div className="text-slate-400 flex items-center justify-center pl-4">
                            <span className="material-symbols-outlined text-xl">search</span>
                        </div>
                        <input 
                            className="form-input w-full border-none bg-transparent focus:ring-0 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 text-sm font-medium px-3" 
                            placeholder="Buscar clientes, tareas o tratos..." 
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (!isContactsPage && e.target.value.trim().length >= 3) {
                                    setShowDropdown(true);
                                }
                            }}
                            onFocus={() => {
                                if (!isContactsPage && searchQuery.trim().length >= 3) {
                                    setShowDropdown(true);
                                }
                            }}
                        />
                    </div>
                </label>

                {/* Dropdown Results */}
                {showDropdown && !isContactsPage && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-xl shadow-2xl border border-slate-200/50 dark:border-slate-800/50 max-h-[350px] overflow-y-auto z-50 py-2 animate-in slide-in-from-top-2 duration-200">
                        {isSearching ? (
                            <div className="flex items-center justify-center py-6 text-slate-400 dark:text-slate-500">
                                <span className="material-symbols-outlined animate-spin mr-2">sync</span>
                                <span className="text-xs font-medium">Buscando...</span>
                            </div>
                        ) : searchResults.length === 0 ? (
                            <div className="px-4 py-6 text-center text-slate-400 dark:text-slate-500">
                                <span className="material-symbols-outlined text-3xl opacity-20 block mb-1">person_search</span>
                                <span className="text-xs">No se encontraron contactos</span>
                            </div>
                        ) : (
                            <div className="flex flex-col">
                                <div className="px-3 pb-1 mb-1 border-b border-slate-100 dark:border-slate-800">
                                    <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Contactos Encontrados ({searchResults.length})</span>
                                </div>
                                {searchResults.map((contact) => {
                                    const initials = contact.full_name ? contact.full_name.substring(0, 2).toUpperCase() : 'CN';
                                    return (
                                        <div
                                            key={contact.id}
                                            onClick={() => {
                                                navigate(`/contacts/${contact.id}/360`);
                                                setSearchQuery('');
                                                setShowDropdown(false);
                                            }}
                                            className="flex items-center gap-3 px-3 py-2 hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer transition-colors"
                                        >
                                            {contact.avatar ? (
                                                <div 
                                                    className="h-8 w-8 rounded-full bg-cover bg-center shrink-0 shadow-sm"
                                                    style={{ backgroundImage: `url("${resolveMediaUrl(contact.avatar)}")` }}
                                                />
                                            ) : (
                                                <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold bg-primary/10 text-primary shrink-0">
                                                    {initials}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">
                                                    {contact.full_name || contact.first_name || 'Sin Nombre'}
                                                </p>
                                                <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                                                    {contact.company || contact.email || contact.phone || 'Sin información adicional'}
                                                </p>
                                            </div>
                                            {contact.pipeline_stage && (
                                                <span className="text-[9px] font-semibold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded shrink-0">
                                                    {contact.pipeline_stage}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-4 lg:gap-6">
                {/* Ayuda. Mismo tamaño y forma que la campana, pero apagado por
                    defecto: es una salida de consulta, no un aviso, y no debe
                    competir por la atención con las notificaciones.
                    Va en <a> y no en botón porque /ayuda/ es un sitio estático
                    fuera del SPA; se abre en otra pestaña para no sacar a nadie
                    de una conversación a medio responder. */}
                <a
                    href="/ayuda/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Ayuda"
                    aria-label="Abrir el manual de ayuda"
                    className="flex size-10 items-center justify-center rounded-full text-slate-400 dark:text-slate-500 transition-all duration-300 hover:bg-primary/10 hover:text-primary"
                >
                    <span className="material-symbols-outlined text-2xl">help</span>
                </a>

                {/* Notifications */}
                <div className="relative flex items-center justify-center" ref={notifRef}>
                    {unreadCount > 0 && (
                        <div className="absolute inset-0 rounded-full bg-primary/30 animate-ripple"></div>
                    )}
                    <button 
                        onClick={() => setIsNotifOpen(!isNotifOpen)}
                        className={`relative flex size-10 items-center justify-center rounded-full transition-all duration-300 ${
                            isNotifOpen ? 'bg-primary text-white shadow-lg' : 'bg-primary/10 text-primary hover:bg-primary/20'
                        }`}
                    >
                        <span className="material-symbols-outlined text-2xl">notifications</span>
                        {unreadCount > 0 && (
                            <span className="absolute top-2 right-2.5 flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                            </span>
                        )}
                    </button>

                    {/* Notifications Dropdown */}
                    {isNotifOpen && (
                        <div className="absolute top-full right-0 mt-3 w-80 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-primary/10 overflow-hidden ring-1 ring-black ring-opacity-5 animate-in slide-in-from-top-2 duration-200">
                            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
                                <div className="flex flex-col">
                                    <h3 className="font-bold text-slate-900 dark:text-white text-sm">Notificaciones</h3>
                                    <span className="text-[10px] text-primary font-bold uppercase tracking-wider">{unreadCount} nuevas</span>
                                </div>
                                {unreadCount > 0 && (
                                    <button 
                                        onClick={() => {
                                            const { markAllRead } = useNotificationStore.getState();
                                            markAllRead();
                                        }}
                                        className="text-[11px] font-bold text-primary hover:text-primary/80 flex items-center gap-1 transition-colors group/all"
                                    >
                                        <span className="material-symbols-outlined text-[16px] group-hover/all:translate-x-0.5 transition-transform">done_all</span>
                                        Limpiar todo
                                    </button>
                                )}
                            </div>
                            <div className="max-h-[400px] overflow-y-auto no-scrollbar">
                                {notifications.length === 0 ? (
                                    <div className="p-8 text-center text-slate-400">
                                        <span className="material-symbols-outlined text-4xl block mb-2 opacity-20">notifications_off</span>
                                        <p className="text-sm">No tienes notificaciones pendientes</p>
                                    </div>
                                ) : (
                                    (notifications || []).map((notif) => {
                                        const isEscalation = notif.data?.type === 'human_escalation';
                                        const isLeadAssigned = notif.data?.type === 'lead_assigned';
                                        const isEmail = notif.data?.type === 'email';
                                        const isInstagram = notif.data?.type === 'instagram_message';
                                        const isFormLead = notif.data?.type === 'form_lead';
                                        const isCampaign = notif.data?.type === 'campaign';
                                        const isAlert = notif.data?.type === 'system_alert';
                                        const isNewLead = notif.data?.type === 'new_lead';

                                        const iconName = isNewLead
                                            ? 'person_add_alt'
                                            : isEscalation
                                            ? 'support_agent'
                                            : isLeadAssigned
                                            ? 'person_add'
                                            : isEmail
                                            ? 'mail'
                                            : isInstagram
                                            ? 'photo_camera'
                                            : isFormLead
                                            ? 'dynamic_form'
                                            : isCampaign
                                            ? 'campaign'
                                            : isAlert
                                            ? 'error'
                                            : 'smart_toy';

                                        const iconBg = isNewLead
                                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600'
                                            : isEscalation
                                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-500'
                                            : isLeadAssigned
                                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-500'
                                            : isFormLead
                                            ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-500'
                                            : isCampaign
                                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600'
                                            : isAlert
                                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600'
                                            : 'bg-primary/10 text-primary';

                                        const borderColor = isNewLead
                                            ? 'border-l-2 border-l-emerald-500'
                                            : isEscalation
                                            ? 'border-l-2 border-l-orange-400'
                                            : isLeadAssigned
                                            ? 'border-l-2 border-l-blue-400'
                                            : isAlert
                                            ? 'border-l-2 border-l-red-500'
                                            : '';

                                        return (
                                            <div 
                                                key={notif.id}
                                                onClick={() => handleNotificationClick(notif)}
                                                className={`p-4 border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors group ${borderColor}`}
                                            >
                                                <div className="flex gap-3">
                                                    <div className={`size-10 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
                                                        <span className="material-symbols-outlined text-[20px]">{iconName}</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className={`text-xs font-bold truncate ${
                                                            isEscalation
                                                                ? 'text-orange-600 dark:text-orange-400'
                                                                : isLeadAssigned
                                                                ? 'text-blue-600 dark:text-blue-400'
                                                                : 'text-slate-900 dark:text-white'
                                                        }`}>
                                                            {notif.data.subject || 'Notificación'}
                                                        </p>
                                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
                                                            {notif.data.message || notif.data.snippet || notif.data.from}
                                                        </p>
                                                        <p className={`text-[10px] mt-1 font-medium ${
                                                            isEscalation 
                                                                ? 'text-orange-400' 
                                                                : isLeadAssigned
                                                                ? 'text-blue-400'
                                                                : 'text-primary/60'
                                                        }`}>
                                                            {dayjs(notif.created_at).fromNow()}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>

                {/* User Info */}
                <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/settings')}>
                    <div className="hidden sm:flex flex-col items-end">
                        <span className="text-sm font-bold text-slate-900 dark:text-slate-100 group-hover:text-primary transition-colors">{user?.name || 'Usuario'}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">{user?.role || 'User'}</span>
                    </div>
                    {user?.avatar ? (
                        <div 
                            className="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-10 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all shadow-sm"
                            style={{ backgroundImage: `url("${resolveMediaUrl(user.avatar)}")` }}
                        ></div>
                    ) : (
                        <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                            {initials}
                        </div>
                    )}
                </div>
            </div>
        </header>

        {/* Mobile Navigation Drawer */}
        <AnimatePresence>
            {isMobileMenuOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="fixed inset-0 bg-black/50 z-[45] lg:hidden backdrop-blur-sm"
                    />
                    
                    {/* Drawer */}
                    <motion.div 
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'tween', duration: 0.3 }}
                        className="fixed top-[69px] left-0 bottom-0 w-64 bg-white dark:bg-surface-dark border-r border-gray-200 dark:border-gray-800 z-[45] lg:hidden flex flex-col overflow-hidden"
                    >
                        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1 custom-scrollbar">
                            {/* Super Admin Section */}
                            {isSuperAdmin && (
                                <>
                                    <div className="flex items-center gap-2 px-3 mb-2">
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-violet-500 dark:text-violet-400 whitespace-nowrap">
                                            Super Admin
                                        </span>
                                        <span className="flex-1 h-px bg-violet-200 dark:bg-violet-800/50" />
                                    </div>
                                    <NavLink
                                        to="/super-admin/tenants"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={({ isActive }) =>
                                            `group relative flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${isActive
                                                ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
                                                : 'text-text-main hover:bg-violet-50 dark:text-gray-300 dark:hover:bg-violet-900/20'
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <span className={`material-symbols-outlined ${isActive ? 'text-violet-600 dark:text-violet-400' : 'text-text-secondary'}`}>
                                                    hub
                                                </span>
                                                <span className="text-sm font-medium">Tenants</span>
                                            </>
                                        )}
                                    </NavLink>
                                    <NavLink
                                        to="/super-admin/sync"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={({ isActive }) =>
                                            `group relative flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${isActive
                                                ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
                                                : 'text-text-main hover:bg-violet-50 dark:text-gray-300 dark:hover:bg-violet-900/20'
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <span className={`material-symbols-outlined ${isActive ? 'text-violet-600 dark:text-violet-400' : 'text-text-secondary'}`}>
                                                    sync_alt
                                                </span>
                                                <span className="text-sm font-medium">Sincronización</span>
                                            </>
                                        )}
                                    </NavLink>
                                    <div className="h-4" />
                                    <div className="flex items-center gap-2 px-3 mb-2">
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">
                                            Sistema
                                        </span>
                                        <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                                    </div>
                                </>
                            )}

                            {/* Regular Nav */}
                            {!isSuperAdmin && navItems.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.path}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={({ isActive }) =>
                                        `group relative flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${isActive
                                            ? 'bg-primary/10 text-primary'
                                            : 'text-text-main hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                                        }`
                                    }
                                >
                                    {({ isActive }) => (
                                        <>
                                            <span className={`material-symbols-outlined ${!isActive ? 'text-text-secondary' : ''} ${item.fill && isActive ? 'fill-1' : ''}`}>
                                                {item.icon}
                                            </span>
                                            <span className="text-sm font-medium">{item.name}</span>
                                            {item.badge > 0 && (
                                                <span
                                                    className="ml-auto min-w-[20px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[11px] font-bold leading-none text-center"
                                                    title={`${item.badge} respuesta(s) de la IA esperando aprobación`}
                                                >
                                                    {item.badge > 99 ? '99+' : item.badge}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>

                        {/* Footer Actions */}
                        <div className="border-t border-gray-200 dark:border-gray-800 p-4 space-y-1">
                            {!isSuperAdmin && (
                                <NavLink
                                    to="/settings"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="group relative flex items-center gap-3 rounded-lg px-3 py-2 text-text-main hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-text-secondary">settings</span>
                                    <span className="text-sm font-medium">Configuración</span>
                                </NavLink>
                            )}

                            <button
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    handleLogout();
                                }}
                                className="group relative w-full flex items-center gap-3 rounded-lg px-3 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                                <span className="material-symbols-outlined">logout</span>
                                <span className="text-sm font-medium">Cerrar sesión</span>
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
        </>
    );
}
